"use client"

import { useState, useCallback, useRef } from "react"
import { useApi } from "@/lib/api"
import { env } from "@/lib/env"
import { logger } from "@/lib/logger"
import type {
  ClientRequestApiResponse,
  ClientRequestDetailDto,
  ClientRequestSearchResult,
} from "@/types/client-requests"
import type { OnboardingSource, OnboardingDetails } from "@/types/onboarding"
import type { UserReportDto } from "@/types/workflow"
import {
  API_SELECT_FIELDS,
  DEFAULT_FACETS,
  transformSearchResultToChangeRequest,
} from "@/types/client-requests"

interface SearchClientRequestsArgs {
  page: number
  pageSize: number
  search?: string
  filter?: string
  orderBy?: string
}

export interface ChangeRequestAttachmentDto {
  name: string
  fileStorageId: string
  extension: string
  size: string
}

export interface SubmitChangeRequestDto {
  requestType: string
  requestDetails: string
  workerFullName?: string
  clientOrganization?: string
  assignedToUserEmail?: string
  assignedToUserName?: string
  fboAssignmentId?: number
  fboWorkOrderId?: number
  fboWorkOrderVersionId?: number
  primaryBillRateUnitCode?: string
  startDate?: string
  endDate?: string
  billRate?: number
  billCurrencyCode?: string
  billRateTypeCode?: string
  terminationDetails?: string
  jobTitle?: string
  hiringManagerName?: string
  hiringManagerEmail?: string
  attachments?: ChangeRequestAttachmentDto[]
}

export interface SubmitChangeRequestResponse {
  id: string
}

export interface FileUploadResponse {
  fileStorageId: string
  name: string
  extension: string
  size: string
}

export function getAttachmentDownloadUrl(requestId: string, fileStorageId: string, token?: string | null): string {
  const baseUrl = env.workflowApiUrl.endsWith('/')
    ? env.workflowApiUrl.slice(0, -1)
    : env.workflowApiUrl
  const url = `${baseUrl}/api/client-request/${requestId}/attachment/${fileStorageId}`
  return token ? `${url}?token=${encodeURIComponent(token)}` : url
}

/**
 * Custom hook for Client Requests API operations.
 * Uses the centralized workflowApi client for automatic token handling and 401 retry.
 */
export function useClientRequestsApi() {
  const { workflowApi, syncApi, onboardingFormApi, fetchWithAuth } = useApi()
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const lastSearchArgsRef = useRef<SearchClientRequestsArgs | null>(null)

  const searchClientRequests = useCallback(
    async (
      page: number,
      pageSize: number,
      search?: string,
      filter?: string,
      orderBy?: string
    ): Promise<ClientRequestApiResponse> => {
      lastSearchArgsRef.current = { page, pageSize, search, filter, orderBy }
      setIsLoading(true)
      setError(null)

      try {
        const requestBody = {
          search: search || "*",
          filter: filter || undefined,
          orderBy: orderBy ?? "RequestDate asc",
          top: pageSize,
          skip: (page - 1) * pageSize,
          select: API_SELECT_FIELDS,
          facets: DEFAULT_FACETS,
          count: true,
        }

        const responseData = await workflowApi.post<any>(
          "/api/client-request/search",
          requestBody
        )

        // Defensive response parsing: array / { data } envelope / { value } OData envelope
        let apiData: ClientRequestSearchResult[]
        let totalCount: number = 0

        if (Array.isArray(responseData)) {
          apiData = responseData
          totalCount = responseData.length
        } else if (responseData && Array.isArray(responseData.data)) {
          apiData = responseData.data
          totalCount =
            responseData["@odata.count"] ||
            responseData.totalCount ||
            responseData.data.length
        } else if (responseData && Array.isArray(responseData.value)) {
          apiData = responseData.value
          totalCount =
            responseData["@odata.count"] ||
            responseData.totalCount ||
            responseData.value.length
        } else {
          apiData = []
          totalCount = 0
        }

        const data = apiData.map(transformSearchResultToChangeRequest)

        // Extract facets if present
        let facets: Record<string, { value: string; count: number }[]> | undefined
        if (
          responseData &&
          responseData["@search.facets"] &&
          typeof responseData["@search.facets"] === "object"
        ) {
          facets = responseData["@search.facets"]
        }

        const hasNextPage = page * pageSize < totalCount
        const hasPreviousPage = page > 1

        return { data, totalCount, hasNextPage, hasPreviousPage, facets }
      } catch (err) {
        const message =
          err instanceof Error ? err.message : "An unknown error occurred"
        setError(message)
        throw err
      } finally {
        setIsLoading(false)
      }
    },
    [workflowApi]
  )

  const refresh = useCallback(async (): Promise<ClientRequestApiResponse | void> => {
    const args = lastSearchArgsRef.current
    if (!args) return
    setIsLoading(true)
    try {
      return await searchClientRequests(
        args.page,
        args.pageSize,
        args.search,
        args.filter,
        args.orderBy
      )
    } finally {
      setIsLoading(false)
    }
  }, [searchClientRequests])

  const getClientRequestDetail = useCallback(
    async (requestId: string): Promise<ClientRequestDetailDto> => {
      return await workflowApi.get<ClientRequestDetailDto>(
        `/api/client-request/${requestId}`
      )
    },
    [workflowApi]
  )

  const acknowledgeRequest = useCallback(
    async (requestId: string): Promise<void> => {
      await workflowApi.post<void>(
        `/api/client-request/${requestId}/acknowledge`,
        {}
      )
    },
    [workflowApi]
  )

  const cancelRequest = useCallback(
    async (requestId: string, comments: string): Promise<void> => {
      await workflowApi.post<void>(
        `/api/client-request/${requestId}/cancel`,
        { comments }
      )
    },
    [workflowApi]
  )

  const holdRequest = useCallback(
    async (requestId: string, comments: string): Promise<void> => {
      await workflowApi.post<void>(
        `/api/client-request/${requestId}/on-hold`,
        { comments }
      )
    },
    [workflowApi]
  )

  const cancelRequestV2 = useCallback(
    async (requestId: string, cancelReason: string): Promise<void> => {
      await syncApi.put<void>(
        `Request/${requestId}/Cancel`,
        { cancelReason }
      )
    },
    [syncApi]
  )

  const holdRequestV2 = useCallback(
    async (requestId: string, onHoldReason: string): Promise<void> => {
      await syncApi.put<void>(
        `Request/${requestId}/PutOnHold`,
        { onHoldReason }
      )
    },
    [syncApi]
  )

  const mapRequestToOnboarding = useCallback(
    async (requestId: string): Promise<OnboardingSource> => {
      return await syncApi.get<OnboardingSource>(`Request/${requestId}/map-to-onboarding`)
    },
    [syncApi]
  )

  // Parallel to mapRequestToOnboarding but for VMS rows. Unlike the
  // ClientRequest path (which goes through gw-sync because gw-sync owns the
  // ClientRequest mirror in its own DB), VMS data only lives in FBO-vms —
  // so this calls our workflow service directly. Avoids a needless gw-sync
  // round-trip that would have just relayed back to FBO-vms anyway.
  //
  // Backend produces the OnboardingSource with SourceType="PreOnboardVms"
  // and code-shaped values for tenant-coded fields (LineOfBusinessCode,
  // BillingRateUnitCode, etc.) since FBO-vms doesn't have access to tenant
  // code tables. The onboarding-form microapp resolves codes → ids
  // client-side from its in-memory tables (loaded from /api/code at mount).
  const mapVmsRequestToOnboarding = useCallback(
    async (vmsRequestId: string): Promise<OnboardingSource> => {
      return await workflowApi.get<OnboardingSource>(`/api/vms/requests/${vmsRequestId}/onboarding-source`)
    },
    [workflowApi]
  )

  const createOnboarding = useCallback(
    async (source: OnboardingSource): Promise<OnboardingDetails> => {
      const result = await onboardingFormApi.post<OnboardingDetails>('/api/onboardings', source)
      if (!result || !result.id) {
        const onboardingError = new Error('Onboarding creation failed')
        logger.error(onboardingError, { hook: 'createOnboarding' })
        throw onboardingError
      }
      return result
    },
    [onboardingFormApi]
  )

  const submitChangeRequest = useCallback(
    async (data: SubmitChangeRequestDto): Promise<SubmitChangeRequestResponse> => {
      return await workflowApi.post<SubmitChangeRequestResponse>(
        "/api/client-request/change-request",
        data
      )
    },
    [workflowApi]
  )

  const updateClientRequestAssignedTo = useCallback(
    async (id: string, user: UserReportDto | null): Promise<void> => {
      const body = user
        ? { email: user.email, fullName: user.fullName }
        : { email: null, fullName: null }
      await workflowApi.put<void>(
        `/api/client-request/${id}/assigned-to`,
        body
      )
    },
    [workflowApi]
  )

  const uploadFile = useCallback(
    async (file: File): Promise<FileUploadResponse> => {
      const formData = new FormData()
      formData.append("file", file)

      const baseUrl = env.workflowApiUrl.endsWith("/")
        ? env.workflowApiUrl.slice(0, -1)
        : env.workflowApiUrl

      return await fetchWithAuth<FileUploadResponse>(
        `${baseUrl}/api/client-request/upload`,
        {
          method: "POST",
          body: formData,
        }
      )
    },
    [fetchWithAuth]
  )

  return {
    searchClientRequests,
    refresh,
    getClientRequestDetail,
    acknowledgeRequest,
    cancelRequest,
    holdRequest,
    cancelRequestV2,
    holdRequestV2,
    mapRequestToOnboarding,
    mapVmsRequestToOnboarding,
    createOnboarding,
    submitChangeRequest,
    updateClientRequestAssignedTo,
    uploadFile,
    isLoading,
    error,
  }
}
